/**
 * Admin Setup → Ownership Transfer (wireframe admin-console-wireframe "Transfer Ownership"; Ownership design v2.5,
 * parts 4, 5 and 8). HR Admin only (admin_setup edit, as the route). The global search at the top right (as JD Archive
 * and Role Reporting) with "Transfer everything from a person"; tabs Jobs Library and People + AI (coming soon). The
 * Jobs Library table is SbTable with its own column filters (the dropdown with a search box on every column, built
 * from the rows), its row selection and pagination; the active filters show as chips with Clear all over SbTable's
 * controlled filteredValue. Columns: Role ID, Job Title, Owner, Status, Approval (Reassign on rows in review),
 * Delegation, Sharing. The list holds every JD of the org (not paginated by the server), so the filters see them all;
 * it is not filtered by the HR Admin's own view scope.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flex, Typography } from 'antd';
import { SearchOutlined, SwapOutlined } from '@ant-design/icons';
import PageHeader from 'Components/PageHeader';
import {
  SbButton, SbNotification, SbSelect, SbSpin, SbTable, SbTabs, SbTag, SbTooltip, TooltipTriggerStyled,
} from 'uiCore';
import { applyColumnFilters } from 'uiCore/SbTable/columnFilters';
import { useHasAccess } from 'Routes/hooks/useHasAccess';
import { getOwnershipJobsLibrary, getTransferCandidates, transferJdOwnership } from 'Api/apiService';
import { PersonAvatarStyled, PersonChipStyled } from 'Components/JdOwnership/JdCurrentOwnerRow.styles';
import { getInitials } from 'Pages/AdminSetup/components/LegalEntityManagement/helper';
import { ComingSoonStyled } from 'Pages/SbJobDetailsV2/SbJobDetailsV2.styles';
import { PageContainerStyled, TableWrapperStyled } from 'styles/common.styles';
import { JdArchiveSearchWrapperStyled } from '../JdArchive/JdArchive.styles';
import {
  CANDIDATES_LOAD_FAILED, CLEAR_ALL, CLEAR_SELECTION, COLUMNS, COMING_SOON, INACTIVE, IN_REVIEW, NOTHING_SELECTED, NO_MATCH,
  NO_ONE, PAGE_SUBTITLE, PAGE_TITLE, REASSIGN_LINK, SEARCH_PLACEHOLDER, SELECT_FIRST, SELECT_HINT, TAB, TAB_LABELS,
  TOOLTIP_REASSIGN, TRANSFER_ALL_BUTTON, TRANSFER_FAILED_TITLE, TRANSFER_PARTIAL_TITLE, TRANSFER_REMOVED_TITLE,
  UNCLAIMED, getAdminError, getApproverText, getCountText, getFilterChips, getSearchOptions, getSelectedText,
  getStatusDisplay, getTransferButton, getTransferredToast, isInReview, matchesSearch, personName,
  summarizeTransfer,
} from 'Utils/ownershipTransfer';
import TransferOwnershipModal from './TransferOwnershipModal';
import TransferEverythingModal from './TransferEverythingModal';
import ReassignApproverModal from './ReassignApproverModal';
import { ControlsStyled, SelectionBarStyled, TabsBarStyled, TransferTableStyled } from './OwnershipTransfer.styles';

const { Text } = Typography;

const Person = ({ person }) => (
  <PersonChipStyled>
    <PersonAvatarStyled aria-hidden="true">{getInitials(person.name || person.email)}</PersonAvatarStyled>
    {person.name || person.email}
  </PersonChipStyled>
);

const People = ({ people }) => (people?.length ? (
  <Flex wrap gap={4}>{people.map((p) => <Person key={p.email} person={p} />)}</Flex>
) : <Text type="secondary">{NO_ONE}</Text>);

const OwnerCell = ({ owner }) => (owner?.email ? (
  <Flex wrap gap={4} align="center">
    {owner.name || owner.email}
    {owner.inactive && <SbTag>{INACTIVE}</SbTag>}
  </Flex>
) : <Text type="secondary" italic>{UNCLAIMED}</Text>);

const OwnershipTransfer = () => {
  const navigate = useNavigate();
  const { hasAccess } = useHasAccess();
  const canTransfer = hasAccess('admin_setup', ['edit']);

  const [tab, setTab] = useState(TAB.JOBS_LIBRARY);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState([]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [everythingOpen, setEverythingOpen] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidatesError, setCandidatesError] = useState(null);
  const candidatesRequest = useRef(0);   // only the latest request's answer is shown
  const [transferring, setTransferring] = useState(false);
  const [reassignRow, setReassignRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOwnershipJobsLibrary();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      SbNotification.error('Could not load the JDs', getAdminError(err, 'Please reload the page.'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canTransfer && rows === null) load();
  }, [canTransfer, rows, load]);

  const allRows = useMemo(() => rows || [], [rows]);
  const searched = useMemo(() => allRows.filter((r) => matchesSearch(r, search)), [allRows, search]);
  const selectedRows = allRows.filter((r) => selected.includes(r.job_id));
  const chips = getFilterChips(filters);

  const columns = useMemo(() => {
    const render = {
      req: (r) => r.sb_requisition_number || NO_ONE,
      title: (r) => <b>{r.title || NO_ONE}</b>,
      owner: (r) => <OwnerCell owner={r.owner} />,
      status: (r) => {
        const pill = getStatusDisplay(r.status);   // the Jobs Library landing page's pill
        return <SbTag color={pill.color} style={pill.style}>{pill.label}</SbTag>;
      },
      approval: (r) => (isInReview(r) ? (
        // one line where it fits (compact rows): the tag, the approver and Reassign
        <Flex wrap gap={4} align="center">
          <SbTag color="gold">{IN_REVIEW}</SbTag>
          <Text type="secondary">{getApproverText(r.approver.name || r.approver.email)}</Text>
          <SbTooltip content={TOOLTIP_REASSIGN}>
            <SbButton type="link" size="small" onClick={() => setReassignRow(r)}>{REASSIGN_LINK}</SbButton>
          </SbTooltip>
        </Flex>
      ) : <Text type="secondary">{NO_ONE}</Text>),
      delegation: (r) => <People people={r.delegate ? [r.delegate] : []} />,
      sharing: (r) => <People people={r.shares} />,
    };
    // SbTable builds each column's filter from filterValue; filteredValue keeps it controlled (the chips clear it)
    return COLUMNS.map((column) => ({
      key: column.key, title: column.title, filterValue: column.filterValue, className: `ownership-col-${column.key}`,
      filteredValue: filters[column.key] ?? null, render: (_, r) => render[column.key](r),
    }));
  }, [filters]);

  // "X of Y JDs": the rows SbTable shows, judged by SbTable's own column filters (no second filter engine)
  const shownCount = useMemo(() => {
    const built = applyColumnFilters(columns, searched);
    return searched.filter((r) => built.every((c) => !(filters[c.key] || []).length
      || filters[c.key].some((value) => c.onFilter(value, r)))).length;
  }, [columns, searched, filters]);

  const openTransfer = async () => {
    candidatesRequest.current += 1;
    const request = candidatesRequest.current;
    setTransferOpen(true);
    setCandidates(null);
    setCandidatesError(null);
    setLoadingCandidates(true);
    try {
      const data = await getTransferCandidates(selectedRows.map((r) => r.job_id));
      if (request === candidatesRequest.current) setCandidates(data);
    } catch (err) {
      if (request === candidatesRequest.current) setCandidatesError(getAdminError(err, CANDIDATES_LOAD_FAILED));
    } finally {
      if (request === candidatesRequest.current) setLoadingCandidates(false);
    }
  };

  // Both popups use the one transfer: the same answer for delegations and shares (carry over, or remove)
  const handleTransfer = async ({ rows: items, newOwner, carry }, name, close) => {
    setTransferring(true);
    try {
      const summary = summarizeTransfer(await transferJdOwnership({
        jobIds: items.map((r) => r.job_id), newOwner, carryDelegation: carry, carryShare: carry,
      }));
      if (summary.moved) SbNotification.success(getTransferredToast(summary.moved, name, carry));
      if (summary.skipped.length) SbNotification.warning(TRANSFER_PARTIAL_TITLE, summary.skipped.join('\n'));
      if (summary.removed.length) SbNotification.info(TRANSFER_REMOVED_TITLE, summary.removed.join('\n'));
      close();
      setSelected([]);
      setRows(null);   // reloads
    } catch (err) {
      SbNotification.error(TRANSFER_FAILED_TITLE, getAdminError(err, 'The ownership could not be transferred.'));
    } finally {
      setTransferring(false);
    }
  };

  if (!canTransfer) {
    return (
      <PageContainerStyled>
        <Flex align="center" justify="center" style={{ minHeight: '60vh' }}>
          <Text type="secondary">You don&apos;t have access to Ownership Transfer.</Text>
        </Flex>
      </PageContainerStyled>
    );
  }

  const n = selectedRows.length;
  // The Jobs Library landing page's layout: the page container, the header, then the table in the shared scrolling
  // wrapper (TableWrapperStyled) with the Jobs Library table wrapper inside, a sticky header and the pagination pinned
  // at the bottom with the "X of Y JDs" count on its left. The filter chips and the selection bar sit above it; the table runs to the bottom of the page.
  const jobsLibrary = (
    <>
      <ControlsStyled>
        {/* The active filters, only when there are any (no empty band above the selection bar) */}
        {chips.length > 0 && (
          <Flex wrap gap={8} align="center" style={{ margin: '0 0 12px' }}>
            {chips.map((chip) => (
              <SbTag key={chip.key} color="blue" closable onClose={() => setFilters((prev) => ({ ...prev, [chip.key]: null }))}>
                {chip.label}
              </SbTag>
            ))}
            {chips.length > 1 && <SbButton size="small" onClick={() => setFilters({})}>{CLEAR_ALL}</SbButton>}
          </Flex>
        )}
        <SelectionBarStyled data-testid="selection-bar">
          {n ? <b>{getSelectedText(n)}</b> : <span>{SELECT_HINT}</span>}
          <SbTooltip content={SELECT_FIRST} disabled={!!n}>
            <TooltipTriggerStyled>
              <SbButton buttonType="primary" size="small" icon={<SwapOutlined />} disabled={!n} onClick={openTransfer}>
                {getTransferButton(n)}
              </SbButton>
            </TooltipTriggerStyled>
          </SbTooltip>
          <SbTooltip content={NOTHING_SELECTED} disabled={!!n}>
            <TooltipTriggerStyled>
              <SbButton size="small" disabled={!n} onClick={() => setSelected([])}>{CLEAR_SELECTION}</SbButton>
            </TooltipTriggerStyled>
          </SbTooltip>
        </SelectionBarStyled>
      </ControlsStyled>
      <TableWrapperStyled>
        {loading && rows === null ? <SbSpin size="large" /> : (
          <TransferTableStyled>
            <SbTable
              columns={columns}
              dataSource={searched}
              loading={loading}
              rowKey="job_id"
              sticky
              size="small"
              rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys), preserveSelectedRowKeys: true }}
              onChange={(_, next) => setFilters(next || {})}
              locale={{ emptyText: NO_MATCH }}
              pagination={{
                showTotal: () => (
                  <div className="paic-pagination-total" data-testid="jd-count">
                    {getCountText(shownCount, allRows.length)}
                  </div>
                ),
              }}
            />
          </TransferTableStyled>
        )}
      </TableWrapperStyled>
    </>
  );

  return (
    <PageContainerStyled>
      <PageHeader
        title={PAGE_TITLE}
        subtitle={PAGE_SUBTITLE}
        onBack={() => navigate('/admin-setup')}
        backLabel="Back to Admin Setup"
        actions={(
          <Flex gap={8} align="center">
            <JdArchiveSearchWrapperStyled>
              <SbSelect
                options={getSearchOptions(allRows)}
                value={search || undefined}
                suffixIcon={<SbButton buttonType="primary" size="small" icon={<SearchOutlined />} />}
                onChange={(value) => setSearch(value ?? '')}
                placeholder={SEARCH_PLACEHOLDER}
                allowClear
                showSearch
                notFoundContent="No matching results"
                aria-label="Search"
              />
            </JdArchiveSearchWrapperStyled>
            <SbButton buttonType="primary" icon={<SwapOutlined />} onClick={() => setEverythingOpen(true)}>
              {TRANSFER_ALL_BUTTON}
            </SbButton>
          </Flex>
        )}
      />
      {/* The tabs are only the tab bar: the active tab's content follows as a direct child of the page container,
          so the table scrolls exactly as on the Jobs Library page */}
      <TabsBarStyled>
        <SbTabs
          activeKey={tab}
          onChange={(key) => { setTab(key); setSelected([]); }}
          items={[
            { key: TAB.JOBS_LIBRARY, label: TAB_LABELS[TAB.JOBS_LIBRARY] },
            { key: TAB.PAIC, label: TAB_LABELS[TAB.PAIC] },
          ]}
        />
      </TabsBarStyled>
      {tab === TAB.JOBS_LIBRARY ? jobsLibrary
        : <ComingSoonStyled><Text type="secondary">{COMING_SOON}</Text></ComingSoonStyled>}
      <TransferOwnershipModal
        open={transferOpen}
        rows={selectedRows}
        candidates={candidates}
        loadingCandidates={loadingCandidates}
        candidatesError={candidatesError}
        transferring={transferring}
        onCancel={() => { candidatesRequest.current += 1; setTransferOpen(false); }}
        onTransfer={({ newOwner, carry }) => handleTransfer({ rows: selectedRows, newOwner, carry },
          personName(candidates, newOwner), () => setTransferOpen(false))}
      />
      <TransferEverythingModal
        open={everythingOpen}
        rows={allRows}
        transferring={transferring}
        onCancel={() => setEverythingOpen(false)}
        onTransfer={({ rows: items, newOwner, carry, newOwnerName }) => handleTransfer({ rows: items, newOwner, carry },
          newOwnerName || newOwner, () => setEverythingOpen(false))}
      />
      <ReassignApproverModal
        open={!!reassignRow}
        row={reassignRow}
        onCancel={() => setReassignRow(null)}
        onReassigned={() => { setReassignRow(null); setRows(null); }}
      />
    </PageContainerStyled>
  );
};

export default OwnershipTransfer;
